import { useCallback, useMemo, useState, MouseEvent } from "react"
import { useInterval } from "usehooks-ts"
import { GetApp } from "../../App"
import { referenceDate, FieldFormat } from "../../App/Format"
import { useEditForm } from "../../App/useEditForm"
import { useSubscription } from "../../App/useSubscription"
import { BlockField, BlockRow } from "../../Components/Block"
import { EditGroup } from "../../Components/EditGroup"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { IAccountSubscriptionStatus, ILogbookAppStatusResult, ITripResponse, ITripUpdateRequest, ApiClient, ShipApi, TripApi } from "../../Types"
import { Subscription } from "../../Types/Generated/LogbookApi/Subscriptions"
import { GetAppView } from "../AppView"
import { GetShipView } from "../ShipView"
import { logger } from "../../App/Logger"
import { TripEditViewProps } from "./Params"
import { useTripData } from "./DataContext"

export const TripEditViewData = {
  saveTrip(api: ApiClient, shipId: number, tripId: number, values: ITripUpdateRequest): Promise<ITripResponse> {
    return new TripApi(api).trips.update({ params: { shipId, tripId }, body: values }).call();
  },
  startNavigation(api: ApiClient) {
    return new ShipApi(api).ships.start({ params: {} }).call();
  },
  // `title` is plain text (issue #529 consistency pass) -- the registered "text"
  // format kind, so it gets the shared "–" convention for a missing value but no
  // unit/locale formatting.
  fieldFormat(): FieldFormat {
    return {
      title: "text",
    };
  },
};

export function TripEditView({ completedCallback, cruiseId: _cruiseId, trip }: TripEditViewProps) {
  const app = GetApp();
  const shipView = GetShipView();
  const { writers } = useTripData();
  const { subscriptionExpired } = GetAppView();
  const t = app.getT("TripEditView");
  const { edited, onEdited, saveEdits } = useEditForm({
    update: writers.updateTrip,
  });
  const [status, setStatus] = useState<boolean>(!!app.status?.time);
  const [time, setTime] = useState<number>(app.status?.time ? Date.parse(app.status.time) : (new Date().valueOf() - new Date(referenceDate).valueOf()));
  const _calculated = trip.calculated;
  const editableFields = [{ name: "title", default: "", type: "string" }];
  const formEditable = app.editable || shipView.shipCloudEditable;
  const tripNotStarted = useMemo(() => formEditable && app.status?.navigation != true && trip.startedAt == undefined, [app.status, formEditable, trip]);

  // Use mock status for testing
  // const accountStatus = useMemo(() => IAccountSubscriptionStatus.active, []);
  // const subscriptionExpired = true; // Mock for testing

  const isTrialExpired = useMemo(() => {
    return app.account?.subscriptionStatus === IAccountSubscriptionStatus.trialExpired;
  }, [app.account?.subscriptionStatus]);

  const isDemoNotStarted = useMemo(() => {
    return app.account?.subscriptionStatus === IAccountSubscriptionStatus.inactive;
  }, [app.account?.subscriptionStatus]);

  const timezone = useMemo(() => {
    return (!trip.startedAt && app.status) ? app.status.systemTimezone : trip.timezone;
  }, [trip, app.status, app.status?.systemTimezone]);
  const currentTime = useMemo(() => time / 1000 + (timezone || 0), [time, trip, timezone]);

  const _cancel = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    completedCallback(false);
  }, [completedCallback]);

  const startNavigation = useCallback(async (e: MouseEvent) => {
    e.preventDefault();
    const result = await TripEditViewData.startNavigation(app.apiClient);
    logger.debug("start navigation", result != undefined);
    if (result) {
      app.resetStatus();
      app.updateCurrent();
    }
  }, [trip]);

  useSubscription((status: ILogbookAppStatusResult, _type: string) => {
    if (status.time) {
      setTime(Date.parse(status.time));
      if (!status) {
        setStatus(true);
      }
    }
  }, Subscription.AppStatus);

  useInterval(() => {
    setTime((new Date().valueOf() - new Date(referenceDate).valueOf()));
  }, status ? null : 5000);

  return (
    <div className="TripEditView">
      <div className="block-container">
        <div className="TripStatsView-block-time block">
          <EditGroup buttonFirst={true} changed={edited} editable={formEditable} editableFields={editableFields} onSave={saveEdits}>

            {tripNotStarted && !isTrialExpired && !subscriptionExpired && (
              <BlockRow className="message block_field_small_50">
                <BlockField>
                  <LocalizedMessage message={(m) => m.currentView.firstTrip} />
                </BlockField>
              </BlockRow>
            )}

            {isTrialExpired && (
              <BlockRow className="message block_field_small_50">
                <BlockField>
                  <LocalizedMessage message={(m) => m.subscription.demoEnded} />
                </BlockField>
              </BlockRow>
            )}
            {subscriptionExpired && !isTrialExpired && (
              <BlockRow className="message block_field_small_50">
                <BlockField>
                  <LocalizedMessage message={(m) => m.subscription.subscriptionExpired} />
                </BlockField>
              </BlockRow>
            )}

            {tripNotStarted && !isTrialExpired && !subscriptionExpired && isDemoNotStarted && app.current && app.current.day > 9251 && (
              <BlockRow className="message_red block_field_small_50">
                <BlockField>
                  <LocalizedMessage message={(m) => m.currentView.demoWillStart} />
                </BlockField>
              </BlockRow>
            )}

            <BlockRow className="block-row-one-row" actions={tripNotStarted}>
              <BlockField label={t("title")} className="block_field_small_28">
                <input defaultValue={trip.title} name="title" onChange={onEdited} readOnly={!formEditable} maxLength={40} />
              </BlockField>

              <BlockField label={t("timezone")} className="block_field_small_10">
                <div className="input">{app.format("timezone", timezone)}</div>
              </BlockField>
              <BlockField label={t("current_time")} className="block_field_small_8">
                <div className="input center">{app.format("timeUtc", currentTime)}</div>
              </BlockField>
              {tripNotStarted && (
                <BlockField>
                  <button className="button default start-trip" onClick={startNavigation} disabled={isTrialExpired}>{t("start")}</button>
                </BlockField>
              )}
            </BlockRow>
          </EditGroup>
        </div>
      </div>
    </div>
  );
}
